import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 10: Love as Root Frequency
 * Large torus (heart field) with central glowing sphere
 * Particle field lines flowing through torus
 * Heartbeat pulse on bass
 */
export default function HeartTorus({ audioData, isActive, position = [0, 0, 0] }) {
  const torusRef = useRef()
  const heartRef = useRef()
  const particlesRef = useRef()

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  // Particle flow through torus
  const particleCount = 400
  const particles = useMemo(() => {
    const positions = new Float32Array(particleCount * 3)

    for (let i = 0; i < particleCount; i++) {
      // Distribute along torus path
      const angle = (i / particleCount) * Math.PI * 2
      const radius = 2
      const tubeRadius = 0.6

      // Add variation in tube position
      const tubeAngle = Math.random() * Math.PI * 2
      const tubeOffset = Math.random() * tubeRadius

      positions[i * 3] = (radius + tubeOffset * Math.cos(tubeAngle)) * Math.cos(angle)
      positions[i * 3 + 1] = tubeOffset * Math.sin(tubeAngle)
      positions[i * 3 + 2] = (radius + tubeOffset * Math.cos(tubeAngle)) * Math.sin(angle)
    }

    return positions
  }, [])

  // Heartbeat timing
  const heartbeatPhase = useRef(0)

  useFrame((state, delta) => {
    if (!isActive) return

    const time = state.clock.elapsedTime

    // Heartbeat rhythm (60 BPM = 1 beat per second, lub-dub pattern)
    heartbeatPhase.current = (time % 1) * 2 * Math.PI

    // Lub-dub pulse shape
    const lub = Math.max(0, Math.sin(heartbeatPhase.current * 2)) * 0.3
    const dub = Math.max(0, Math.sin(heartbeatPhase.current * 2 - Math.PI * 0.5)) * 0.15
    const heartPulse = lub + dub

    // Enhanced pulse on audio bass
    const totalPulse = heartPulse + bass * 0.3

    if (torusRef.current) {
      // Gentle rotation
      torusRef.current.rotation.y += delta * 0.08
      torusRef.current.rotation.x = Math.sin(time * 0.3) * 0.15

      // Pulse scale
      const scale = 1 + totalPulse
      torusRef.current.scale.setScalar(scale)

      // Emissive intensity with pulse
      torusRef.current.material.emissiveIntensity = 0.5 + totalPulse * 1.5
    }

    if (heartRef.current) {
      // Central heart sphere pulses
      const heartScale = 1 + totalPulse * 0.5
      heartRef.current.scale.setScalar(heartScale)

      // Intense glow on pulse
      heartRef.current.material.emissiveIntensity = 2 + totalPulse * 3

      // Rotate independently
      heartRef.current.rotation.y += delta * 0.15
    }

    // Animate particles flowing through torus
    if (particlesRef.current) {
      const positions = particlesRef.current.geometry.attributes.position.array

      for (let i = 0; i < particleCount; i++) {
        // Rotate particles around torus
        const idx = i * 3
        const x = positions[idx]
        const z = positions[idx + 2]

        // Rotation speed varies with radius
        const currentRadius = Math.sqrt(x * x + z * z)
        const rotSpeed = delta * (0.15 + amplitude * 0.1)

        const angle = Math.atan2(z, x) + rotSpeed
        positions[idx] = currentRadius * Math.cos(angle)
        positions[idx + 2] = currentRadius * Math.sin(angle)

        // Gentle vertical oscillation
        positions[idx + 1] += Math.sin(time * 0.8 + i * 0.1) * 0.002
      }

      particlesRef.current.geometry.attributes.position.needsUpdate = true
    }
  })

  return (
    <group position={position}>
      {/* Main torus (heart field) */}
      <mesh ref={torusRef}>
        <torusGeometry args={[2, 0.6, 32, 100]} />
        <meshStandardMaterial
          color="#FF69B4"
          emissive="#FF1493"
          emissiveIntensity={0.5}
          transparent
          opacity={0.7}
          metalness={0.6}
          roughness={0.3}
        />
      </mesh>

      {/* Central heart sphere */}
      <mesh ref={heartRef}>
        <sphereGeometry args={[0.5, 32, 32]} />
        <meshStandardMaterial
          color="#FFB6C1"
          emissive="#FF1493"
          emissiveIntensity={2}
          transparent
          opacity={0.9}
        />
      </mesh>

      {/* Particle field lines */}
      <points ref={particlesRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={particleCount}
            array={particles}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.04}
          color="#FFAACC"
          transparent
          opacity={0.8}
          sizeAttenuation
          blending={THREE.AdditiveBlending}
        />
      </points>

      {/* Heart glow light */}
      <pointLight position={[0, 0, 0]} intensity={3} color="#FF69B4" distance={8} />

      {/* Torus rim lights */}
      <pointLight position={[2, 0, 0]} intensity={1} color="#FF69B4" distance={4} />
      <pointLight position={[-2, 0, 0]} intensity={1} color="#FF69B4" distance={4} />
      <pointLight position={[0, 0, 2]} intensity={1} color="#FF69B4" distance={4} />
      <pointLight position={[0, 0, -2]} intensity={1} color="#FF69B4" distance={4} />
    </group>
  )
}
